import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { UbicacionDigitalService } from '../../services/ubicacion-digital/ubicacion-digital.service';
import { DialogService } from '../../shared/services/dialog.service';
import { UbicacionDigital } from '../../domain/ubicacion-digital.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-ubicaciones-digitales',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './ubicaciones-digitales.component.html',
  styleUrls: ['./ubicaciones-digitales.component.scss']
})
export class UbicacionesDigitalesComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  ubicaciones = signal<UbicacionDigital[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: UbicacionDigital[] = [];
  filterValue: string = '';

  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000;

  columnsTable: Column[] = [
    { field: 'codigo', header: 'Código', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombre', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'canal', header: 'Canal', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'active', header: 'Activo', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private ubicacionDigitalService: UbicacionDigitalService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';
  }

  cargar(): void {
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.ubicacionDigitalService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          const ubicacionesMapeadas = members.map((ubicacion: any) => ({
            id: ubicacion.id,
            codigo: ubicacion.codigo,
            nombre: ubicacion.nombre,
            canal: ubicacion.canal,
            descripcion: ubicacion.descripcion,
            active: ubicacion.active ?? true,
            selected: false
          }));

          this.ubicaciones.set(ubicacionesMapeadas);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || ubicacionesMapeadas.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar ubicaciones digitales (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar ubicaciones digitales (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.ubicaciones.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleUbicacionActiva(ubicacion: UbicacionDigital): void {
    if (!ubicacion.id) return;

    const nuevoEstado = ubicacion.active ?? true;

    const ubicacionActualizada: UbicacionDigital = {
      active: nuevoEstado
    };

    this.loading.set(true);
    this.ubicacionDigitalService.patch(ubicacion.id, ubicacionActualizada)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: UbicacionDigital) => {
          this.loading.set(false);
          const estadoFinal = resp.active ?? false;
          const accionTxt = estadoFinal ? 'activada' : 'desactivada';
          this.messageService.add({
            severity: 'success',
            summary: `Ubicación ${accionTxt}`,
            detail: `La ubicación digital ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          ubicacion.active = !nuevoEstado;
          console.error('Error al actualizar ubicación digital:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado de la ubicación digital',
            life: 3000
          });
        }
      });
  }

  editarUbicacion(id: number): void {
    this.router.navigate(['/ubicaciones-digitales/edit', id]);
  }

  navigateToCreateUbicacion(): void {
    this.router.navigate(['/ubicaciones-digitales/edit/nuevo']);
  }

  eliminarUbicacion(ubicacion: UbicacionDigital): void {
    const displayName = ubicacion.codigo ? `${ubicacion.codigo} - ${ubicacion.nombre}` : ubicacion.nombre;
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactivar la ubicación digital ${displayName || ''}?`,
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!ubicacion.id) return;

        this.loading.set(true);
        const ubicacionActualizada: UbicacionDigital = {
          active: false
        };

        this.ubicacionDigitalService.patch(ubicacion.id, ubicacionActualizada)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Ubicación Digital Desactivada',
                detail: 'La ubicación digital ha sido desactivada correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error al desactivar ubicación digital:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactivar la ubicación digital',
                life: 3000
              });
            }
          });
      }
    });
  }

  activarUbicacion(ubicacion: UbicacionDigital): void {
    if (!ubicacion.id) return;

    this.loading.set(true);
    const ubicacionActualizada: UbicacionDigital = {
      active: true
    };

    this.ubicacionDigitalService.patch(ubicacion.id, ubicacionActualizada)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Ubicación Digital Activada',
            detail: 'La ubicación digital ha sido activada correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al activar ubicación digital:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activar la ubicación digital',
            life: 3000
          });
        }
      });
  }
}
